/* Loesner koblinger til en slettet bankutskrift — speiler
 * functions/bank/bankStatementDeletion.ts.
 *
 * Brukes av klient-fallbacken i src/services/backend-bank-lines.jsx naar
 * backend-skrivingen ikke er tilgjengelig. Regelen er den samme begge steder: en
 * bokfoert foering staar paa egne bein og skal overleve at utskriften forsvinner,
 * men `bankTrace`-en mot den slettede utskriften maa bort. Blir den staaende,
 * faller foeringen utenfor `!posting.statementId`-klausulen i kontrollisten og
 * blir usynlig for enhver senere utskrift.
 */
(function registerBankStatementDeletionRelease(scope) {
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const text = (value) => (typeof value === 'string' ? value.trim() : '');

  const traceBelongsToStatement = (trace, statementId) =>
    Boolean(trace) && typeof trace === 'object' && text(trace.statementId) === statementId;

  const releaseTraces = (value, statementId) => {
    if (Array.isArray(value)) {
      let changed = false;
      const next = value.map((entry) => {
        const released = releaseTraces(entry, statementId);
        if (released !== entry) changed = true;
        return released;
      });
      return changed ? next : value;
    }
    if (!value || typeof value !== 'object') return value;
    let changed = false;
    const next = {};
    Object.entries(value).forEach(([key, entry]) => {
      if (key === 'bankTrace' && traceBelongsToStatement(entry, statementId)) { changed = true; return; }
      if (key === 'bankTraces' && Array.isArray(entry)) {
        const kept = entry.filter((trace) => !traceBelongsToStatement(trace, statementId));
        if (kept.length === entry.length) { next[key] = entry; return; }
        changed = true;
        if (kept.length) next[key] = kept;
        return;
      }
      const released = releaseTraces(entry, statementId);
      if (released !== entry) changed = true;
      next[key] = released;
    });
    return changed ? next : value;
  };

  const bookedMatchGroupBlocking = (state, statementId) => safeArray(state?.bankMatchGroups)
    .some((group) => text(group?.status) === 'booked' &&
      safeArray(group?.bankLines).some((ref) => text(ref?.statementId) === statementId));

  const postingIsTracedToStatement = (posting, statementId) =>
    text(posting?.status) !== 'annulled' && (
      traceBelongsToStatement(posting?.bankTrace, statementId) ||
      safeArray(posting?.bankTraces).some((trace) => traceBelongsToStatement(trace, statementId))
    );

  // Godkjente matchinggrupper kan ikke avbrytes riktig uten backend-mutasjonen,
  // saa fallbacken stopper der i stedet for aa etterlate en halv opprydding.
  const approvedMatchGroupBlocking = (state, statementId) => safeArray(state?.bankMatchGroups)
    .some((group) => text(group?.status) === 'approved' &&
      safeArray(group?.bankLines).some((ref) => text(ref?.statementId) === statementId));

  const detachBankStatementLinks = (state, statementId, nowIso) => {
    const id = text(statementId);
    const detached = new Set(safeArray(state?.bankReconciliationPostings)
      .filter((posting) => postingIsTracedToStatement(posting, id))
      .map((posting) => text(posting?.id))
      .filter(Boolean));
    const released = releaseTraces(state, id);
    const now = typeof nowIso === 'function' ? nowIso() : new Date().toISOString();
    return {
      ...released,
      bankReconciliationPostings: safeArray(released?.bankReconciliationPostings).map((posting) =>
        detached.has(text(posting?.id)) && !posting?.bankTrace && !safeArray(posting?.bankTraces).length
          ? { ...posting, status: 'avvik', detachedFromStatementAt: now, updatedAt: now }
          : posting),
    };
  };

  scope.PlummeBankStatementDeletion = {
    bookedMatchGroupBlocking,
    approvedMatchGroupBlocking,
    detachBankStatementLinks,
  };
})(typeof window !== 'undefined' ? window : globalThis);
